import React, { useEffect, useRef, useState } from 'react';
import { IoChevronBack, IoChevronForward } from "react-icons/io5";

const HorizontalShelf = ({
  children,
  isDarkMode,
  step = 560,
  className = '',
  contentClassName = '',
}) => {
  const shelfRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const node = shelfRef.current;
    if (!node) {
      return;
    }

    const { scrollLeft, clientWidth, scrollWidth } = node;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
  }, [children]);

  useEffect(() => {
    const node = shelfRef.current;
    if (!node) {
      return undefined;
    }

    const handleResize = () => updateScrollState();

    node.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', handleResize);

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(handleResize)
      : null;

    resizeObserver?.observe(node);

    return () => {
      node.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }, []);

  const handleScroll = (direction) => {
    shelfRef.current?.scrollBy({
      left: direction * step,
      behavior: 'smooth',
    });
  };

  const controlClassName = isDarkMode
    ? 'border-white/10 bg-[#0f1729]/92 text-slate-100 hover:bg-[#16213a]'
    : 'border-slate-200/80 bg-white/92 text-slate-700 hover:bg-slate-50';

  return (
    <div className={`relative ${className}`}>
      <div className={`pointer-events-none absolute inset-y-2 left-0 z-[1] w-10 rounded-l-[22px] transition-opacity ${
        canScrollLeft ? 'opacity-100' : 'opacity-0'
      } ${
        isDarkMode
          ? 'bg-gradient-to-r from-[#081120] via-[#081120]/88 to-transparent'
          : 'bg-gradient-to-r from-white/95 via-white/82 to-transparent'
      }`} />

      <div className={`pointer-events-none absolute inset-y-2 right-0 z-[1] w-10 rounded-r-[22px] transition-opacity ${
        canScrollRight ? 'opacity-100' : 'opacity-0'
      } ${
        isDarkMode
          ? 'bg-gradient-to-l from-[#081120] via-[#081120]/88 to-transparent'
          : 'bg-gradient-to-l from-white/95 via-white/82 to-transparent'
      }`} />

      <button
        type="button"
        onClick={() => handleScroll(-1)}
        aria-label="Scroll left"
        className={`absolute -left-1 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border shadow-lg backdrop-blur transition md:flex ${controlClassName} ${canScrollLeft ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      >
        <IoChevronBack size={18} />
      </button>

      <button
        type="button"
        onClick={() => handleScroll(1)}
        aria-label="Scroll right"
        className={`absolute -right-1 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border shadow-lg backdrop-blur transition md:flex ${controlClassName} ${canScrollRight ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      >
        <IoChevronForward size={18} />
      </button>

      <div
        ref={shelfRef}
        className={`flex gap-4 overflow-x-auto px-1 pb-2 snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${contentClassName}`}
      >
        {children}
      </div>
    </div>
  );
};

export default HorizontalShelf;
